import React, { useEffect, useState, useRef } from "react";
import CustomButton from "../../../components/CustomButton";
import iconClose from "url:shared-assets/icons/close.svg";
import iconMicOn from "url:shared-assets/icons/video/mic-on.svg";
import iconMicOff from "url:shared-assets/icons/video/mic-off.svg";
import animationSound from "url:shared-assets/animations/sound.svg";
import animationProcessing from "url:shared-assets/animations/processing.svg";
import animationListening from "url:shared-assets/animations/listening.svg";
import { MessageInterface, FunctionCallInterface } from "../interfaces";

interface PropsInterface {
  ephemeralKey: string;
  saveMessage: (msg: MessageInterface) => void;
  language: string;
  close: () => void;
}

// https://platform.openai.com/docs/guides/realtime
// https://learn.microsoft.com/en-us/azure/ai-services/openai/how-to/realtime-audio-webrtc

const Voice = (props: PropsInterface) => {
  const [voiceState, setVoiceState] = useState<string>("initializing");
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [micOn, setMicOn] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    initSession()
      .then(() => console.log("Opened voice session"))
      .catch((error) => {
        console.error(error);
        setError(error);
      });
  }, []);

  async function initSession() {
    // Create a peer connection
    const pc = new RTCPeerConnection();

    // Set up to play remote audio from the model
    audioElement.current = document.createElement("audio");
    audioElement.current.autoplay = true;
    // @ts-ignore
    pc.ontrack = (e) => (audioElement.current.srcObject = e.streams[0]);

    // Add local audio track for microphone input in the browser
    const ms = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    pc.addTrack(ms.getTracks()[0]);

    // Set up data channel for sending and receiving events
    const dc = pc.createDataChannel("oai-events");
    setDataChannel(dc);

    // Start the session using the Session Description Protocol (SDP)
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // const url = "https://swedencentral.openai.azure.com/openai/v1/realtime/calls";
    const url = "https://dazzleai.openai.azure.com/openai/v1/realtime/calls";

    // const url = "https://api.openai.com/v1/realtime/calls";
    const sdpResponse = await fetch(url, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${props.ephemeralKey}`,
        "Content-Type": "application/sdp",
      },
    });

    const answer: RTCSessionDescriptionInit = {
      type: "answer",
      sdp: await sdpResponse.text(),
    };
    await pc.setRemoteDescription(answer);

    peerConnection.current = pc;
  }

  const toggleMic = () => {
    const newState = !micOn;
    setMicOn(newState);
    if (peerConnection.current) {
      peerConnection.current.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.enabled = newState;
        }
      });
    }
  };

  // Stop current session, clean up peer connection and data channel
  function stopSession() {
    if (dataChannel) {
      dataChannel.close();
    }

    if (peerConnection.current) {
      peerConnection.current.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });
    }

    if (peerConnection.current) {
      peerConnection.current.close();
    }

    setDataChannel(null);
    peerConnection.current = null;
    props.close();
  }

  // Send a message to the model
  const sendClientMessage = (message: string) => {
    if (dataChannel) {
      const event = {
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              text: message,
            },
          ],
        },
      };
      console.log(event);
      dataChannel.send(JSON.stringify(event));
      const eventCreate = {
        type: "response.create",
        response: {
          // modalities: ["audio", "text"],
          instructions: "Reply based on the user's input."
        },
      };
      console.log(eventCreate);
      dataChannel.send(JSON.stringify(eventCreate));
    } else {
      console.error(
        "Failed to send message - no data channel available",
        message
      );
    }
  };

  // Attach event listeners to the data channel when a new one is created
  useEffect(() => {
    if (dataChannel) {
      // Append new server events to the list
      dataChannel.addEventListener("message", (e) => {
        const event = JSON.parse(e.data);

        let role = "";
        let mess = "";
        let functionCall: FunctionCallInterface | undefined;

        // console.log('New event: ' + event.type);
        // console.log(event);

        if (event.type === "conversation.item.input_audio_transcription.completed") {
          role = "user";
          mess = event.transcript;
        } else if (event.type === "response.output_audio_transcript.done") {
          role = "assistant";
          mess = event.transcript;
        } else if (event.type === "response.function_call_arguments.done") {
          setVoiceState("done_talking");
          role = "assistant";
          const function_name: string = event.name;
          const args = JSON.parse(event.arguments);
          functionCall = {
            name: function_name,
            arguments: args,
          };
          let clientMessage = "";
          if (function_name === "create_task") {
            mess = "Created task: " + args.name;
            clientMessage = "Give me a short summary of the action you just created";
          } else if (function_name === "edit_task") {
            mess = "Edited task: " + args.name;
            clientMessage = "Give me a short summary of what you just changed in the action";
          }
          if (clientMessage.length > 0) {
            sendClientMessage(clientMessage);
            setVoiceState("thinking");
          }
        // } else {
        //   console.log('Unhandled event: ' + event.type);
        //   console.log(event);
        }
        if (role !== "") {
          // console.log(event)
          const msg: MessageInterface = {
            role: role,
            message: mess,
            sent: new Date(),
            functionCall: functionCall,
            aiFile: [],
          };
          props.saveMessage(msg);
        } else {
          if (event.type === "input_audio_buffer.speech_started") {
            setVoiceState("recording");
          } else if (event.type === "input_audio_buffer.speech_stopped") {
            setVoiceState("thinking");
          } else if (event.type === "response.done") {
            setVoiceState("done_thinking");
          } else if (event.type === "response.audio.done") {
            setVoiceState("listening");
          } else if (event.type === "output_audio_buffer.started") {
            setVoiceState("done_thinking");
          } else if (event.type === "output_audio_buffer.stopped") {
            setVoiceState("done_talking");
          }
          // if (event.type !== 'response.audio_transcript.delta') console.log(event)
        }
      });

      // Set session active when the data channel is opened
      dataChannel.addEventListener("open", () => {
        setVoiceState("listening");
        // sendClientMessage("Ask what language you should use.")
      });
    }
  }, [dataChannel]);

  const formatError = (err: Error) => {
    let msg = err.message;
    if (err.name === "NotAllowedError") {
      msg =
        props.language === "sv"
          ? "Du behöver ge din browser tillgång till mikrofonen"
          : "You need to give your browser access to your microphone";
    }
    return (
      <div className="mt-4 border rounded-md border-cherry-500 p-4">{msg}</div>
    );
  };

  const formatState = () => {
    let msg = "Unknown state...";
    let animation: string | null = null;
    if (error) {
      msg = error.name;
    } else if (!micOn) {
      msg =
        props.language === "sv"
          ? "Din mikrofon är avstängd"
          : "Your microphone is muted";
      animation = animationProcessing;
    } else if (voiceState === "initializing") {
      msg =
        props.language === "sv"
          ? "Kontaktar AI..."
          : "Calling AI, please hold...";
      animation = animationProcessing;
    } else if (voiceState === "listening" || voiceState === "done_talking") {
      msg = props.language === "sv" ? "Lyssnar..." : "Listening...";
      animation = animationListening;
    } else if (voiceState === "thinking") {
      msg = props.language === "sv" ? "Tänker..." : "Thinking...";
      animation = animationProcessing;
    } else if (voiceState === "done_thinking") {
      msg = props.language === "sv" ? "Pratar..." : "Speaking...";
      animation = animationSound;
    } else if (voiceState === "recording") {
      msg = props.language === "sv" ? "Lyssnar..." : "Listening...";
      animation = animationSound;
    }
    return (
      <div className="mt-4">
        <div className="mt-8 flex justify-center">
          <div className="flex items-center w-40 h-40">
            {animation && <img src={animation} alt="animation" />}
          </div>
        </div>
        <p className="mt-2 z-title1 text-center">{msg}</p>
        {error ? formatError(error) : ""}
      </div>
    );
  };

  const micImg = micOn ? iconMicOn : iconMicOff;

  return (
    <div
      className="fixed w-full h-full"
      style={{
        top: 0,
        left: 0,
        backgroundColor: "rgba(0, 0, 38, 1.0)",
        zIndex: 1001,
      }}
    >
      <div className="flex items-center justify-center h-full pb-40">
        {formatState()}
      </div>

      <div
        className="text-center absolute w-full"
        style={{ bottom: 40, left: 0 }}
      >
        <div className="flex mx-auto justify-center">
          <CustomButton
            size="lg"
            leftIcon={<img src={micImg} alt="mic" className="p-1" />}
            isRound={true}
            buttonType="transparentBase"
            onClick={() => toggleMic()}
          />

          <CustomButton
            size="lg"
            leftIcon={
              <img src={iconClose} alt="close" className="h-6 w-10 px-2" />
            }
            className="ml-2"
            isRound={true}
            buttonType="transparentBase"
            onClick={() => stopSession()}
          />
        </div>
      </div>
    </div>
  );
};

export default Voice;
